import {
  Alert,
  AlertColor,
  Box,
  CssBaseline,
  Snackbar,
  ThemeProvider,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { Navigate, Route, Routes } from "react-router-dom";
import { InternContainerQuery } from "./__generated__/InternContainerQuery.graphql";
import { BrandWorkspaceProvider } from "../../contexts/BrandWorkspaceContext";
import {
  ColorModeProvider,
  useColorMode,
} from "../../contexts/ColorModeContext";
import SnackbarContext from "../../contexts/SnackbarContext";
import { applyCssVariables, createInternTheme } from "../../styles/InternTheme";
import InternBrand from "./InternBrand";
import InternBrandWorkspace from "./InternBrandWorkspace";
import InternDashboard from "./InternDashboard";
import InternHome from "./InternHome";
import InternJobs from "./InternJobs";
import InternPrivacyWebhooks from "./InternPrivacyWebhooks";
import {
  InternMobileHeader,
  InternSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
} from "./components";

const query = graphql`
  query InternContainerQuery {
    user {
      isStaff
    }
    ...InternHome_query
  }
`;

// Inner component that uses the color mode context
const InternContainerInner = () => {
  const data = useLazyLoadQuery<InternContainerQuery>(query, {});
  const { colorMode } = useColorMode();
  const theme = useMemo(() => createInternTheme(colorMode), [colorMode]);

  // Apply CSS variables when color mode changes
  useEffect(() => {
    applyCssVariables(colorMode);
  }, [colorMode]);

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleMobileOpen = useCallback(() => setMobileOpen(true), []);
  const handleMobileClose = useCallback(() => setMobileOpen(false), []);

  // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("info");
  const openSnackbar = useCallback(
    (message: string, severity: AlertColor) => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
    },
    [setSnackbarMessage, setSnackbarSeverity]
  );
  const snackbarProvider = useMemo(() => ({ openSnackbar }), [openSnackbar]);

  const isStaff = data.user?.isStaff ?? false;
  if (!isStaff) {
    return <Navigate to="/login" />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarContext.Provider value={snackbarProvider}>
        {/* Snackbar */}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={3000}
          open={snackbarMessage !== null}
          onClose={() => setSnackbarMessage(null)}
        >
          <Alert
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
            onClose={() => setSnackbarMessage(null)}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Layout */}
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            bgcolor: "var(--intern-bg-secondary)",
          }}
        >
          {/* Sidebar */}
          <InternSidebar
            mobileOpen={mobileOpen}
            onMobileClose={handleMobileClose}
          />

          {/* Mobile Header */}
          <InternMobileHeader onMenuClick={handleMobileOpen} />

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              minWidth: 0, // Prevent flex item from overflowing
              // Account for mobile header
              pt: { xs: "56px", md: 0 },
              // Full width on mobile, account for sidebar on desktop
              width: {
                xs: "100%",
                md: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
              },
            }}
          >
            <Box
              sx={{
                flex: 1,
                p: { xs: 1.5, sm: 2, md: 3 },
                maxWidth: "1600px",
                width: "100%",
                mx: "auto",
                overflow: "hidden", // Prevent horizontal scroll on container
              }}
            >
              <Routes>
                {/* Platform Overview - dashboard */}
                <Route
                  path="/"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternDashboard />
                    </Suspense>
                  }
                />

                {/* Platform Jobs Monitor */}
                <Route
                  path="/jobs"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternJobs />
                    </Suspense>
                  }
                />

                {/* All Brands list */}
                <Route
                  path="/brands/"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternHome query={data} />
                    </Suspense>
                  }
                />

                {/* Privacy Webhook Events */}
                <Route
                  path="/privacy"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternPrivacyWebhooks />
                    </Suspense>
                  }
                />

                {/* Legacy Brand detail route (keep for backward compatibility) */}
                <Route
                  path="/brands/:id/*"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternBrand />
                    </Suspense>
                  }
                />

                {/* Brand Workspace routes */}
                <Route
                  path="/workspace/*"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InternBrandWorkspace />
                    </Suspense>
                  }
                />
              </Routes>
            </Box>
          </Box>
        </Box>
      </SnackbarContext.Provider>
    </ThemeProvider>
  );
};

// Wrapper that provides the ColorModeProvider and BrandWorkspaceProvider
const InternContainer = () => {
  return (
    <ColorModeProvider>
      <BrandWorkspaceProvider>
        <InternContainerInner />
      </BrandWorkspaceProvider>
    </ColorModeProvider>
  );
};

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      color: "var(--intern-text-tertiary)",
    }}
  >
    Loading...
  </Box>
);

export default InternContainer;
